import React, { useState } from 'react';
import { Calendar, Phone, ExternalLink, Link as LinkIcon, Star, CreditCard, Mail, MapPin, User, X, UserCheck, Building2, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

const Links = () => {
  const { t } = useLanguage();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      console.log('Attempting to insert call back request:', formData);
      
      const { data, error } = await supabase
        .from('call_back_requests')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        }]);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Call back request submission successful:', data);
      toast({
        title: "Επιτυχής αποστολή!",
        description: "Θα επικοινωνήσουμε μαζί σας σύντομα.",
      });
      
      setIsFormOpen(false);
      setFormData({ name: '', email: '', phone: '' });
    } catch (error) {
      console.error('Error submitting call back request:', error);
      toast({
        title: "Σφάλμα",
        description: "Κάτι πήγε στραβά. Παρακαλώ δοκιμάστε ξανά.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const contactLinks = [
    {
      icon: <User className="w-5 h-5" />,
      label: "Ζήτα να σε καλέσουμε",
      action: () => setIsFormOpen(true),
      isButton: true
    },
    {
      icon: <Calendar className="w-5 h-5" />,
      label: "Κλείσε ένα ραντεβού",
      href: "https://calendly.com/michaelfoto-contact/30min"
    },
    {
      icon: <Phone className="w-5 h-5" />,
      label: "WhatsApp",
      href: "https://wa.me/306986904312"
    },
    {
      icon: <Phone className="w-5 h-5" />,
      label: "Viber",
      href: "viber://chat?number=+306986904312"
    },
    {
      icon: <UserCheck className="w-5 h-5" />,
      label: "Αποθήκευσε την Επαφή",
      href: "https://drive.google.com/file/d/1NQW-9eFMhL-9jT5pfgNah0v_FxPPLc7R/view?usp=sharing"
    }
  ];

  const businessLinks = [
    {
      icon: <LinkIcon className="w-5 h-5" />,
      label: "TapG.gr",
      href: "https://www.tapg.gr"
    },
    {
      icon: <LinkIcon className="w-5 h-5" />,
      label: "RentMySite.gr",
      href: "https://www.rentmysite.gr"
    },
    {
      icon: <LinkIcon className="w-5 h-5" />,
      label: "Upplead.gr",
      href: "https://www.upplead.gr"
    },
    {
      icon: <Star className="w-5 h-5" />,
      label: "Άφησε μια αξιολόγηση",
      href: "https://cutt.ly/YrAfWcR6"
    }
  ];

  const paymentLinks = [
    {
      icon: <CreditCard className="w-5 h-5" />,
      label: "Κάνε την πληρωμή σου",
      href: "https://buy.stripe.com/bJe4gycHL0MwbdmcU5abK03"
    }
  ];

  return (
    <div className="min-h-screen bg-black flex flex-col items-center py-12 px-4 animate-fade-in">
      {/* Back to home button */}
      <div className="fixed top-6 left-6 z-10">
        <Link to="/" className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors">
          <ExternalLink size={18} />
          <span>Επιστροφή</span>
        </Link>
      </div>
      
      <div className="w-full max-w-md mx-auto flex flex-col items-center">
        {/* Profile */}
        <Avatar className="w-28 h-28 rounded-full border-2 border-brand mb-4">
          <AvatarImage src="/lovable-uploads/d7fc06bc-6e33-4278-a9fa-cd33ea091583.png" alt="Mike Foto" className="w-full h-full object-cover" />
          <AvatarFallback className="bg-dark-300 text-white">MF</AvatarFallback>
        </Avatar>
        
        {/* Name and Tagline */}
        <h1 className="text-2xl font-bold text-white mb-1">Mike Foto</h1>
        <p className="text-gray-400 mb-2 text-center">Brand Strategist & Digital Marketing Specialist</p>
        <p className="text-gray-500 mb-8 text-center text-sm">Συνιδρυτής των TapG.gr, RentMySite.gr και Upplead.gr</p>
        
        {/* About */}
        <div className="w-full mb-8 p-4 bg-dark-300 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-brand" />
            <h2 className="text-white font-semibold">Ποιος είμαι</h2>
          </div>
          <p className="text-gray-300 text-sm text-left">Κατασκευάζω επαγγελματικά eshop και ιστοσελίδες για επιχειρήσεις που θέλουν να έχουν σοβαρή online παρουσία από την πρώτη μέρα. Βοηθάω επιχειρηματίες, brands και startups να ξεκινήσουν σωστά, με καθαρή στρατηγική, σύγχρονο design και τα κατάλληλα εργαλεία: ιστοσελίδες, eshop, Google reviews με NFC, funnels, short-form βίντεο και διαφήμιση. Ό,τι χρειάζεται μια επιχείρηση για να ξεκινήσει ή να πουλήσει online  το φτιάχνω, το παραδίδω και το υποστηρίζω.</p>
        </div>

        {/* Contact Links */}
        <div className="w-full space-y-3 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Phone className="w-5 h-5 text-brand" />
            <h3 className="text-white font-semibold">Επικοινωνία</h3>
          </div>
          {contactLinks.map((link, index) => (
            link.isButton ? (
              <Button
                key={index}
                onClick={link.action}
                className="w-full bg-dark-300 hover:bg-dark-400 text-white justify-start gap-4 transition-all px-4 py-6 h-auto animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {link.icon} 
                <span>{link.label}</span>
              </Button>
            ) : (
              <Button
                key={index}
                asChild
                className="w-full bg-dark-300 hover:bg-dark-400 text-white justify-start gap-4 transition-all px-4 py-6 h-auto animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <a href={link.href} target="_blank" rel="noopener noreferrer">
                  {link.icon} 
                  <span>{link.label}</span>
                </a>
              </Button>
            )
          ))}
        </div>

        {/* Business Links */}
        <div className="w-full space-y-3 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-brand" />
            <h3 className="text-white font-semibold">Οι Δραστηριότητές μου</h3>
          </div>
          {businessLinks.map((link, index) => (
            <Button
              key={index}
              asChild
              className="w-full bg-dark-300 hover:bg-dark-400 text-white justify-start gap-4 transition-all px-4 py-6 h-auto animate-fade-in"
              style={{ animationDelay: `${(contactLinks.length + index) * 100}ms` }}
            >
              <a href={link.href} target="_blank" rel="noopener noreferrer">
                {link.icon} 
                <span>{link.label}</span>
              </a>
            </Button>
          ))}
        </div>

        {/* Payment Links */}
        <div className="w-full space-y-3 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-brand" />
            <h3 className="text-white font-semibold">Πληρωμή</h3>
          </div>
          {paymentLinks.map((link, index) => (
            <Button
              key={index}
              asChild
              className="w-full bg-brand hover:bg-brand-hover text-white justify-start gap-4 transition-all px-4 py-6 h-auto animate-fade-in"
              style={{ animationDelay: `${(contactLinks.length + businessLinks.length + index) * 100}ms` }}
            >
              <a href={link.href} target="_blank" rel="noopener noreferrer">
                {link.icon} 
                <span>{link.label}</span>
              </a>
            </Button>
          ))}
        </div>

        {/* Contact Info */}
        <div className="w-full p-4 bg-dark-300 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-5 h-5 text-brand" />
            <h3 className="text-white font-semibold">Στοιχεία Επικοινωνίας</h3>
          </div>
          <div className="space-y-2 text-sm text-left">
            <div className="flex items-center gap-2 text-gray-300">
              <Mail className="w-4 h-4" />
              <span>michaelfoto.contact@gmail.com</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <Phone className="w-4 h-4" />
              <span>+30 698 690 4312</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <MapPin className="w-4 h-4" />
              <span>Πλατεία Δημοκρατίας 5-9, Θεσσαλονίκη 54630</span>
            </div>
          </div>
        </div>
      </div>

      {/* Call Request Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="bg-dark-300 text-white border-gray-700">
          <DialogHeader>
            <DialogTitle>Ζήτα να σε καλέσουμε</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Όνομα</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="bg-dark-400 border-gray-600 text-white"
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="bg-dark-400 border-gray-600 text-white"
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Τηλέφωνο</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="bg-dark-400 border-gray-600 text-white"
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-brand hover:bg-brand-hover flex-1" disabled={isSubmitting}>
                {isSubmitting ? "Αποστολή..." : "Αποστολή"}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setIsFormOpen(false)}>
                Ακύρωση
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Links;
